import { useEffect, useState, useRef, useMemo } from 'react';
import AnalyticsService from '../../analytics/AnalyticsService';
import { getOrder } from '../../redux/api-v2/OrdersApi';
import { getCustomer } from '../../redux/api-v2/CustomersApi';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  OrderFlow,
  OrderFlowTabs,
  setOrderDetails
} from '../../redux/orderFlow/orderFlowSlice';
import { fetchCountries } from '../../redux/api-v2';
import {
  resetMainContract,
  resetExtraContract
} from '../../redux/api-v2/ContractsApi';
import { fetchProducts } from '../../redux/api-v2/ProductsApi';
import { SessionManager } from '../../utils/session-manager';
import ExtendedInsurance from './tabs/ExtendedInsurance';
import ExtensionFlow from './tabs/ExtensionFlow';
import ExtraPersonalDetails from './tabs/ExtraPersonalDetails';
import HealthCard from './tabs/HealthCard';
import IdentificationDetails from './tabs/IdentificationDetails';
import LoadingTab from './tabs/LoadingTab';
import OrderSummary from './tabs/OrderSummary';
import Payment from './tabs/Payment';
import PersonalDetails from './tabs/PersonalDetails';
import PersonalDetailsConfirmation from './tabs/PersonalDetailsConfirmation';
import TravelDetails from './tabs/TravelDetails';
import ErrorTab from './tabs/ErrorTab';
import { navigateToNextTab } from '../../utils/navigation-helpers';
import { createSessionInfo } from '../../analytics/payload';

const normalizeDateFormat = (dateString: string): string => {
  if (!dateString) return dateString;

  if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    return dateString.split('T')[0];
  }

  const separators = ['/', '-'];
  for (const sep of separators) {
    if (dateString.includes(sep)) {
      const parts = dateString.split(sep);
      if (parts.length === 3) {
        const [day, month, year] = parts;
        if (day.length <= 2 && month.length <= 2 && year.length === 4) {
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
    }
  }

  return dateString;
};

const decodeJWTPayload = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

const FormController = () => {
  const dispatch = useAppDispatch();
  const orderDetails = useAppSelector(state => state.orderFlow);
  const { currentTab, flow, ...orderDetailsWithoutHandlers } = orderDetails;
  const mainContractApiCall = useAppSelector(
    state => state.contracts.mainContract
  );
  const extraContractApiCall = useAppSelector(
    state => state.contracts.extraContract
  );
  const orderApiCall = useAppSelector(state => state.orders.order);
  const customerApiCall = useAppSelector(state => state.customers.customer);
  const productsState = useAppSelector(state => state.products.products);
  const urlParams = new URLSearchParams(window.location.search);
  const [errorMessage, setErrorMessage] = useState('');
  const extendedInsurance = urlParams.get('extendedInsurance');
  const token = urlParams.get('token');
  const orderId = urlParams.get('orderId');
  const query = urlParams.toString();
  const userId = SessionManager.getUserId();
  const orderDetailsWithUserSession = {
    ...orderDetailsWithoutHandlers,
    userSession: userId
  };
  const pageLoadStartTime = useRef<number>(new Date().getTime());
  const previousTabRef = useRef<OrderFlowTabs | null>(null);

  useEffect(() => {
    dispatch(fetchCountries());
  }, []);

  useEffect(() => {
    const productsLoaded =
      Array.isArray(productsState.response) &&
      productsState.response.length > 0;
    if (!productsLoaded && productsState.loading !== 'pending') {
      dispatch(fetchProducts());
    }
  }, [dispatch, productsState]);

  useEffect(() => {
    const pageLoadEndTime = new Date().getTime();
    const pageLoadTime = pageLoadEndTime - pageLoadStartTime.current;
    SessionManager.trackPage(window.location.href);
    SessionManager.incrementPageVisitCount();

    if (previousTabRef.current !== null) {
      SessionManager.setOriginTab(OrderFlowTabs[previousTabRef.current]);
    }
    const sessionPayload = createSessionInfo(pageLoadTime, query);
    AnalyticsService.sendLog(OrderFlowTabs[currentTab], sessionPayload);
    previousTabRef.current = currentTab;
  }, [currentTab, query]);

  useEffect(() => {
    if (orderId?.length && token?.length) {
      dispatch(getOrder({ orderId, token }));

      const payload = decodeJWTPayload(token);
      if (payload?.user_id) {
        dispatch(getCustomer({ customerId: payload.user_id, token }));
      }
    }
  }, [orderId, token, dispatch]);

  useEffect(() => {
    if (
      orderApiCall.loading === 'pending' ||
      customerApiCall.loading === 'pending'
    ) {
      navigateToNextTab(dispatch, OrderFlowTabs.Loading);
    } else if (orderApiCall.loading === 'failed') {
      setErrorMessage(orderApiCall.error?.i18nMessage ?? 'api.error.server_error');
      navigateToNextTab(dispatch, OrderFlowTabs.Error);
    } else if (customerApiCall.loading === 'failed') {
      setErrorMessage(customerApiCall.error?.i18nMessage ?? 'api.error.server_error');
      navigateToNextTab(dispatch, OrderFlowTabs.Error);
    }
  }, [
    orderApiCall.loading,
    orderApiCall.error?.i18nMessage,
    customerApiCall.loading,
    customerApiCall.error?.i18nMessage,
    dispatch
  ]);

  useEffect(() => {
    const orderSucceeded =
      orderApiCall.loading === 'succeeded' && orderApiCall.response;
    const customerSucceeded =
      customerApiCall.loading === 'succeeded' && customerApiCall.response;
    const productsSucceeded =
      productsState.loading === 'succeeded' &&
      Array.isArray(productsState.response);

    if (orderSucceeded && customerSucceeded && productsSucceeded) {
      const orderResponse = orderApiCall.response as any;
      const customerResponse = customerApiCall.response as any;

      let mappedProducts = orderResponse.productsOrdered;

      if (
        Array.isArray(orderResponse.productsOrdered) &&
        Array.isArray(productsState.response)
      ) {
        mappedProducts = orderResponse.productsOrdered.map(
          (orderProductId: any) => {
            const matchedProduct = productsState.response.find(
              (product: any) => product.id === orderProductId
            );
            return matchedProduct ? matchedProduct.productId : orderProductId;
          }
        );
      }

      const combinedData = {
        firstName: customerResponse.firstName,
        lastName: customerResponse.lastName,
        email: customerResponse.email,
        phoneNumber: customerResponse.phoneNumber,
        documentNumber: customerResponse.idCard,
        documentType: customerResponse.documentType,
        birthday: normalizeDateFormat(customerResponse.birthday),
        newsConsent: customerResponse.newsConsent,

        departureDate: normalizeDateFormat(orderResponse.departureDate),
        returnDate: normalizeDateFormat(orderResponse.returnDate),
        homeCountry: orderResponse.homeCountryCode,
        destinationCountry: orderResponse.destinationCountryCode,
        originalProducts: orderResponse.productsOrdered,
        discountCode: orderResponse.discountCode,
        healthInsurance: orderResponse.ehic,
        legalConsentOne: orderResponse.legalConsentOne,
        legalConsentTwo: orderResponse.legalConsentTwo,
        flow: OrderFlow.Flow2
      };

      dispatch(setOrderDetails(combinedData));
      navigateToNextTab(dispatch, OrderFlowTabs.PersonalDetailsConfirmation);
    }
  }, [
    orderApiCall.loading,
    orderApiCall.response,
    customerApiCall.loading,
    customerApiCall.response,
    productsState.loading,
    productsState.response,
    dispatch
  ]);

  useEffect(() => {
    if (extendedInsurance === 'true') {
      navigateToNextTab(dispatch, OrderFlowTabs.ExtensionFlow);
    }
  }, [extendedInsurance]);

  useEffect(() => {
    if (mainContractApiCall.loading === 'succeeded') {
      dispatch(resetMainContract());
      navigateToNextTab(dispatch, OrderFlowTabs.Payment);
    } else if (mainContractApiCall.loading === 'pending') {
      navigateToNextTab(dispatch, OrderFlowTabs.Loading);
    } else if (mainContractApiCall.loading === 'failed') {
      setErrorMessage(mainContractApiCall.error?.i18nMessage ?? 'api.error.server_error');
      navigateToNextTab(dispatch, OrderFlowTabs.Error);
    }
  }, [
    mainContractApiCall.loading,
    mainContractApiCall.response,
    mainContractApiCall.error?.i18nMessage,
    dispatch
  ]);
  useEffect(() => {
    if (extraContractApiCall.loading === 'succeeded') {
      dispatch(resetExtraContract());
      navigateToNextTab(dispatch, OrderFlowTabs.Payment);
    } else if (extraContractApiCall.loading === 'pending') {
      navigateToNextTab(dispatch, OrderFlowTabs.Loading);
    } else if (extraContractApiCall.loading === 'failed') {
      setErrorMessage(extraContractApiCall.error?.i18nMessage ?? 'api.error.server_error');
      navigateToNextTab(dispatch, OrderFlowTabs.Error);
    }
  }, [
    extraContractApiCall.loading,
    extraContractApiCall.response,
    extraContractApiCall.error?.i18nMessage,
    dispatch
  ]);
  const tabComponents = useMemo(
    () => ({
      [OrderFlowTabs.PersonalDetails]: <PersonalDetails />,
      [OrderFlowTabs.TravelDetails]: <TravelDetails />,
      [OrderFlowTabs.IdentificationDetails]: <IdentificationDetails />,
      [OrderFlowTabs.ExtraPersonalDetails]: <ExtraPersonalDetails />,
      [OrderFlowTabs.PersonalDetailsConfirmation]: (
        <PersonalDetailsConfirmation />
      ),
      [OrderFlowTabs.HealthCard]: <HealthCard />,
      [OrderFlowTabs.ExtendedInsurance]: <ExtendedInsurance />,
      [OrderFlowTabs.OrderSummary]: <OrderSummary />,
      [OrderFlowTabs.ExtensionFlow]: <ExtensionFlow />,
      [OrderFlowTabs.Payment]: <Payment />,
      [OrderFlowTabs.Loading]: <LoadingTab />,
      [OrderFlowTabs.Error]: <ErrorTab error={errorMessage} />
    }),
    [errorMessage]
  );

  return tabComponents[currentTab] || <div>Default</div>;
};

export default FormController;
